import { z } from 'zod';
import { apiKeyViewSchema, partnerRefSchema, planViewSchema } from './types';

// Organizations and billing shapes, from social-admin/docs/api.md.

export const ORG_STATUSES = ['ACTIVE', 'SUSPENDED', 'CLOSED'] as const;
export type OrgStatus = (typeof ORG_STATUSES)[number];

export const planRefSchema = z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
  status: z.enum(['ACTIVE', 'ARCHIVED']),
  owner_partner_id: z.string().nullable(),
});
export type PlanRef = z.infer<typeof planRefSchema>;

export const orgViewSchema = z.object({
  id: z.string(),
  name: z.string(),
  external_ref: z.string().nullable(),
  status: z.enum(ORG_STATUSES),
  timezone: z.string(),
  partner_id: z.string(),
  plan_id: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type OrgView = z.infer<typeof orgViewSchema>;

export const orgRowSchema = z.object({
  id: z.string(),
  name: z.string(),
  external_ref: z.string().nullable(),
  status: z.enum(ORG_STATUSES),
  partner: partnerRefSchema,
  plan: planRefSchema.nullable(),
  connected_accounts: z.number(),
  members: z.number(),
  created_at: z.string(),
});
export type OrgRow = z.infer<typeof orgRowSchema>;

export const orgDetailSchema = z.object({
  org: orgViewSchema,
  partner: partnerRefSchema,
  plan: planViewSchema.nullable(),
  direct: z.boolean(),
  limits: z.array(
    z.object({
      key: z.string(),
      label: z.string(),
      used: z.number(),
      limit: z.number().nullable(),
      at_limit: z.boolean(),
    }),
  ),
  usage_from: z.string(),
  usage_to: z.string(),
  month_to_date: z.record(z.string(), z.number()),
  transfers: z.array(
    z.object({
      id: z.string(),
      from_partner: partnerRefSchema,
      to_partner: partnerRefSchema,
      reason: z.string(),
      performed_by: z.string(),
      created_at: z.string(),
    }),
  ),
  assignable_plans: z.array(planViewSchema),
});
export type OrgDetail = z.infer<typeof orgDetailSchema>;

export const transferPreviewSchema = z.object({
  org: orgViewSchema,
  partner: partnerRefSchema,
  plan: planRefSchema.nullable(),
  plan_will_be_dropped: z.boolean(),
  targets: z.array(partnerRefSchema),
});
export type TransferPreview = z.infer<typeof transferPreviewSchema>;

export const transferResultSchema = z.object({
  org: orgViewSchema,
  from_partner: partnerRefSchema,
  to_partner: partnerRefSchema,
  plan_dropped: z.boolean(),
});
export type TransferResult = z.infer<typeof transferResultSchema>;

export const orgKeysSchema = z.object({
  org: orgViewSchema,
  partner: partnerRefSchema,
  direct: z.boolean(),
  keys: z.array(apiKeyViewSchema),
  available_permissions: z.array(z.string()),
});
export type OrgKeys = z.infer<typeof orgKeysSchema>;

export interface CreateDirectOrgRequest {
  name: string;
  external_ref: string | null;
  plan_id: string;
  timezone: string;
}

const billingTotalsSchema = z.object({
  targets_published: z.number(),
  posts_published: z.number(),
});

export const billingSchema = z.object({
  month: z.string(),
  current_month: z.boolean(),
  partner: partnerRefSchema.nullable(),
  lines: z.array(
    z.object({
      partner_id: z.string(),
      partner_name: z.string(),
      org_id: z.string(),
      org_name: z.string(),
      targets_published: z.number(),
      posts_published: z.number(),
    }),
  ),
  totals: billingTotalsSchema,
});
export type Billing = z.infer<typeof billingSchema>;

export const partnerBillingSchema = z.object({
  month: z.string(),
  current_month: z.boolean(),
  partners: z.array(
    z.object({
      partner_id: z.string(),
      partner_name: z.string(),
      targets_published: z.number(),
      posts_published: z.number(),
    }),
  ),
  totals: billingTotalsSchema,
});
export type PartnerBilling = z.infer<typeof partnerBillingSchema>;
